import { KnownSearchResultItemDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';

/**
 * Loose front-end typing for a search result's `subType` — the server emits it as a plain string
 * (values sourced from ProjectType/UserType/DeliveryType/OrderStatus/HubType), so this mirrors the
 * known values without any wire-level guarantee. See FRONT-END-SEARCH-MAPPING.md.
 */
export enum SearchResultItemDtoSubType {
  // Project (ProjectType)
  Project = 'project',
  Precinct = 'precinct',
  Portfolio = 'portfolio',
  ResidentialBuilding = 'residential-building',
  CommercialBuilding = 'commercial-building',
  Retailer = 'retailer',
  Store = 'store',
  Business = 'business',

  // Person (Account + UserType)
  Account = 'account',
  User = 'user',
  Occupant = 'occupant',
  Courier = 'courier',
  Customer = 'customer',

  // Delivery (DeliveryType)
  InboundDryCleaning = 'inbound-dry-cleaning',
  OutboundDryCleaning = 'outbound-dry-cleaning',
  InboundMail = 'inbound-mail',
  OutboundMail = 'outbound-mail',
  InboundParcel = 'inbound-parcel',
  PeerToPeer = 'peer-to-peer',
  RetailOrder = 'retail-order',
  LockerpointOrder = 'lockerpoint-order',

  // Order fallback (OrderStatus)
  Booked = 'booked',
  InProgress = 'in-progress',
  Delivered = 'delivered',
  DeliveredOffline = 'delivered-offline',
  Overdue = 'overdue',
  Collected = 'collected',
  RetrievalRequired = 'retrieval-required',
  Retrieved = 'retrieved',
  Cancelled = 'cancelled',

  // Hub (HubType)
  LockersHub = 'lockers-hub',
  SpacesHub = 'spaces-hub',
  KiosklessSpacesHub = 'kioskless-spaces-hub',
  ExternalLockersHub = 'lockers-hub-external'
}

/**
 * Order sections are rendered in. The server concatenates results in this type order with no
 * cross-type re-sort, so we keep the same order and group into sections per type.
 */
export const RESULT_TYPE_ORDER: string[] = [
  KnownSearchResultItemDtoType.Project,
  KnownSearchResultItemDtoType.Hub,
  KnownSearchResultItemDtoType.UserOrAccount,
  KnownSearchResultItemDtoType.Delivery,
  KnownSearchResultItemDtoType.Order
];

export const RESULT_TYPE_SECTION_TITLE: Record<string, string> = {
  [KnownSearchResultItemDtoType.Project]: 'Projects',
  [KnownSearchResultItemDtoType.UserOrAccount]: 'People & Accounts',
  [KnownSearchResultItemDtoType.Hub]: 'Hubs',
  [KnownSearchResultItemDtoType.Delivery]: 'Deliveries',
  [KnownSearchResultItemDtoType.Order]: 'Orders'
};

/** Each section shows this many rows before "show more" reveals the rest (up to the fetched limit). */
export const INITIAL_VISIBLE_PER_SECTION = 5;

/** Human-facing role label keyed on a `user-or-account` result's `subType`. */
export const PERSON_ROLE_LABEL: Record<string, string> = {
  [SearchResultItemDtoSubType.Account]: 'Account',
  [SearchResultItemDtoSubType.Occupant]: 'Occupant',
  [SearchResultItemDtoSubType.Courier]: 'Courier',
  [SearchResultItemDtoSubType.Customer]: 'Customer',
  [SearchResultItemDtoSubType.User]: 'User'
};

/** Preferred display order for per-subType person sections — team members (accounts) lead. */
export const PERSON_SUBTYPE_ORDER: string[] = [
  SearchResultItemDtoSubType.Account,
  SearchResultItemDtoSubType.Occupant,
  SearchResultItemDtoSubType.Customer,
  SearchResultItemDtoSubType.Courier,
  SearchResultItemDtoSubType.User
];

/** Section header title for each `user-or-account` subType. */
export const PERSON_SUBTYPE_SECTION_TITLE: Record<string, string> = {
  [SearchResultItemDtoSubType.Account]: 'Team members',
  [SearchResultItemDtoSubType.Occupant]: 'Occupants',
  [SearchResultItemDtoSubType.Courier]: 'Couriers',
  [SearchResultItemDtoSubType.Customer]: 'Customers',
  [SearchResultItemDtoSubType.User]: 'Users'
};
